import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type {
  AddAdvanceCorrectionInvoiceRequestBody,
  AddAdvanceOrProformaInvoiceRequestBody,
  AddFinalCorrectionInvoiceRequestBody,
  AddFinalInvoiceRequestBody,
  InvoiceResponse,
} from "../../../api/api";
import {
  addOrderAdvanceCorrectionInvoice,
  addOrderAdvanceInvoice,
  addOrderFinalCorrectionInvoice,
  addOrderFinalInvoice,
  addOrderProformaInvoice,
  deleteInvoice,
} from "../../../api/api";
import { invalidateOnOrderChange } from "../invaidateOnOrderChange";
import type { SavedOrder } from "../orderTypes";
import { orderPriceByDepartmentQueryKey } from "./useOrderPriceByDepartment";

export const useOrderInvoices = (order: Pick<SavedOrder, "id" | "start" | "invoices">) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const addAdvanceInvoiceMutation = useMutation<InvoiceResponse, unknown, AddAdvanceOrProformaInvoiceRequestBody>({
    mutationFn: (r) => addOrderAdvanceInvoice(order.id, r),
    onSuccess: async (data) => {
      enqueueSnackbar(`Faktura zaliczkowa ${data.name} została utworzona`, { variant: "success" });
      await invalidateOnOrderChange(queryClient, order);
      await queryClient.invalidateQueries({ queryKey: orderPriceByDepartmentQueryKey(order.id) });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się utworzyć faktury zaliczkowej. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const addProformaInvoiceMutation = useMutation<InvoiceResponse[], unknown, AddAdvanceOrProformaInvoiceRequestBody>({
    mutationFn: (r) => addOrderProformaInvoice(order.id, r),
    onSuccess: async (data) => {
      enqueueSnackbar(`Faktura proforma ${data.map((i) => i.name).join(", ")} została utworzona`, {
        variant: "success",
      });
      await invalidateOnOrderChange(queryClient, order);
      await queryClient.invalidateQueries({ queryKey: orderPriceByDepartmentQueryKey(order.id) });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się utworzyć faktury proforma. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const addAdvanceCorrectionInvoiceMutation = useMutation<
    InvoiceResponse[],
    unknown,
    AddAdvanceCorrectionInvoiceRequestBody
  >({
    mutationFn: (body) => addOrderAdvanceCorrectionInvoice(order.id, body),
    onSuccess: async (data) => {
      if (data.length > 0) {
        enqueueSnackbar(`Faktura zaliczkowa korygująca  ${data.map((i) => i.name).join(", ")} została utworzona`, {
          variant: "success",
        });
      } else {
        enqueueSnackbar(`Faktura zaliczkowa korygująca nie została wystawiona. Brak faktur zaliczkowych.`, {
          variant: "warning",
        });
      }
      await invalidateOnOrderChange(queryClient, order);
      await queryClient.invalidateQueries({ queryKey: orderPriceByDepartmentQueryKey(order.id) });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się utworzyć faktury zaliczkowej korygującej. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const addFinalInvoiceMutation = useMutation<InvoiceResponse[], unknown, AddFinalInvoiceRequestBody>({
    mutationFn: (r) => addOrderFinalInvoice(order.id, r),
    onSuccess: async (data) => {
      enqueueSnackbar(`Faktura końcowa ${data.map((v) => v.name).join(", ")} została utworzona`, {
        variant: "success",
      });
      await invalidateOnOrderChange(queryClient, order);
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się utworzyć faktury końcowej. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const addFinalCorrectionInvoiceMutation = useMutation<
    InvoiceResponse[],
    unknown,
    AddFinalCorrectionInvoiceRequestBody
  >({
    mutationFn: (r) => addOrderFinalCorrectionInvoice(order.id, r),
    onSuccess: async (data) => {
      enqueueSnackbar(`Faktura końcowa korygująca ${data.map((i) => i.name).join(", ")} została utworzona`, {
        variant: "success",
      });
      await invalidateOnOrderChange(queryClient, order);
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się utworzyć faktury końcowej korygującej. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteInvoiceMutation = useMutation<InvoiceResponse[], unknown, { invoiceId: string }>({
    mutationFn: (m) => deleteInvoice(order.id, m.invoiceId),
    onSuccess: async () => {
      enqueueSnackbar("Faktura została usunięta", { variant: "success" });
      await invalidateOnOrderChange(queryClient, order);
    },
  });

  return {
    addAdvanceInvoice: (r: AddAdvanceOrProformaInvoiceRequestBody) => addAdvanceInvoiceMutation.mutateAsync(r),
    addProformaInvoice: (r: AddAdvanceOrProformaInvoiceRequestBody) => addProformaInvoiceMutation.mutateAsync(r),
    addAdvanceCorrectionInvoice: (body: AddAdvanceCorrectionInvoiceRequestBody) =>
      addAdvanceCorrectionInvoiceMutation.mutateAsync(body),
    addFinalInvoice: (r: AddFinalInvoiceRequestBody) => addFinalInvoiceMutation.mutateAsync(r),
    addFinalCorrectionInvoice: (r: AddFinalCorrectionInvoiceRequestBody) =>
      addFinalCorrectionInvoiceMutation.mutateAsync(r),
    deleteInvoice: (invoiceId: string) => deleteInvoiceMutation.mutateAsync({ invoiceId }),
  };
};
